import { labelScale } from '../state';

// The reader's own text size. On iPhone and iPad that is Dynamic Type (Settings › Display & Brightness
// › Text Size, and Larger Text in Accessibility), read through the -apple-system-body font. Elsewhere it
// is the browser's default size, which Chrome scales with the phone's font size because index.html opts
// in with <meta name="text-scale" content="scale">.

/** Dynamic Type: the Apple system font styles, on a touch screen (a Mac's Safari has the styles but not the setting). */
const dynamicType = () => typeof CSS !== 'undefined' && CSS.supports('font', '-apple-system-body') && navigator.maxTouchPoints > 0;

/** Preferred text size as a multiple of the default (17 px body on Apple devices, 16 px elsewhere). */
function readTextScale(): number {
  if (dynamicType()) {
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;font:-apple-system-body';
    document.body.appendChild(probe);
    const px = parseFloat(getComputedStyle(probe).fontSize);
    probe.remove();
    if (px > 0) return px / 17;
  }
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return root > 0 ? root / 16 : 1;
}

/**
 * How far each part of the interface follows it. Reading text (cards, lists, lessons) goes up to double;
 * the bars, pills and dock only a little, as iOS does with its own bars; names on the sky in between,
 * since larger ones crowd each other out. Smaller than default stops at 90%.
 */
export function textScales(s: number) {
  const clamp = (lo: number, hi: number) => Math.min(hi, Math.max(lo, s));
  return { text: clamp(0.9, 2), ui: clamp(0.9, 1.25), sky: clamp(0.9, 1.4), large: s >= 1.3 };
}

function apply() {
  const t = textScales(readTextScale());
  const root = document.documentElement;
  root.style.setProperty('--ts', t.text.toFixed(3));
  root.style.setProperty('--ts-ui', t.ui.toFixed(3));
  root.classList.toggle('text-large', t.large);
  labelScale.value = t.sky;
}

/** Set the sizes now, and again whenever the page comes back (the setting may have changed meanwhile). */
export function watchTextScale() {
  apply();
  addEventListener('resize', apply);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && apply());
}
